import { normalizeSiteConfigJson } from "../../Utils/siteConfig";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { X, Calendar, Save, Ban } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import SearchableCombobox from "Components/SearchableCombobox";
import { useGetPostesEmission } from "../../hooks/emission/useGetPostesEmission";
import { useGetTypeIndicateurs } from "../../hooks/metadata/useGetTypeIndicators";
import { useGetPosteDimensions } from "../../hooks/metadata/useGetPosteDimensions";
import { useCreateSaisie } from "../../hooks/saisie/useCreateSaisie";
import { useGetSaisies } from "../../hooks/saisie/useGetSaisies";
import { useUpdateSaisie } from "../../hooks/saisie/useUpdateSaisie";
import { useGetSites } from "../../hooks/site/useGetSites";
import type { Site } from "../../types/site";
import type { TypeIndicateur } from "../../types/typeIndicateurs";
import type {
  CreateSaisieRequest,
  UpdateSaisieRequest,
  Saisie,
  PostesData,
  DimensionSelection,
} from "../../types/saisie";
import "./DataEntryDialog.css";

interface DataEntryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  site: Site;
  userRole: string;
}

interface IndicatorEntry {
  id: string;
  value: string;
  selections: DimensionSelection[];
}

interface IndicatorData {
  [indicatorId: number]: {
    entries: IndicatorEntry[];
    isDraft: boolean;
  };
}

interface PosteWithIndicators {
  poste: {
    id: number;
    name: string;
  };
  indicators: Array<TypeIndicateur & { obligatoire?: boolean }>;
}

export const DataEntryDialog: React.FC<DataEntryDialogProps> = ({
  isOpen,
  onClose,
  site,
  userRole,
}) => {
  console.log("[DataEntryDialog] Dialog rendered with props:", {
    isOpen,
    siteId: site?.id,
    userRole,
    timestamp: new Date().toISOString(),
  });

  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [indicatorData, setIndicatorData] = useState<IndicatorData>({});
  const [existingSaisie, setExistingSaisie] = useState<Saisie | null>(null);
  const [activePosteId, setActivePosteId] = useState<number | null>(null);
  const isInitialOpen = useRef(true);
  const previousMonth = useRef<number | null>(null);
  const previousYear = useRef<number | null>(null);

  // Track dialog open/close state changes
  useEffect(() => {
    console.log("[DataEntryDialog] Dialog state changed:", {
      isOpen,
      siteId: site?.id,
      timestamp: new Date().toISOString(),
    });

    if (!isOpen) {
      console.log("[DataEntryDialog] Dialog closed - clearing form data only");
      // Clear form data when dialog closes but preserve month/year selection
      setIndicatorData({});
      setExistingSaisie(null);
      isInitialOpen.current = true; // Reset for next open
      // Don't reset month/year - let user continue where they left off
    }
  }, [isOpen, site?.id]);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Fetch fresh site data when dialog opens
  const { data: freshSiteData, refetch: refetchSites } = useGetSites();

  // Refetch sites data when dialog opens to ensure we have the latest config
  useEffect(() => {
    if (isOpen && site?.id) {
      console.log("[DataEntryDialog] Dialog opened - refetching sites data");
      refetchSites();
    }
  }, [isOpen, site?.id, refetchSites]);

  const currentSite = useMemo(() => {
    // Use the fresh site data if available, otherwise fall back to the prop
    if (freshSiteData && site?.id) {
      const updatedSite = freshSiteData.find((s) => s.id === site.id);
      if (updatedSite) {
        console.log("[DataEntryDialog] Using fresh site data:", {
          siteId: updatedSite.id,
          hasConfig: !!updatedSite.config_json,
          configType: typeof updatedSite.config_json,
          configIsObject:
            updatedSite.config_json &&
            typeof updatedSite.config_json === "object" &&
            !Array.isArray(updatedSite.config_json),
          configKeys:
            updatedSite.config_json &&
            typeof updatedSite.config_json === "object" &&
            !Array.isArray(updatedSite.config_json)
              ? Object.keys(updatedSite.config_json)
              : [],
        });
        return updatedSite;
      }
    }
    console.log("[DataEntryDialog] Using prop site data:", {
      siteId: site?.id,
      hasConfig: !!site?.config_json,
      configType: typeof site?.config_json,
    });
    return site;
  }, [freshSiteData, site]);

  // Fetch data
  const { data: postesEmission } = useGetPostesEmission();
  const { data: typeIndicateurs } = useGetTypeIndicateurs();

  // Fetch existing saisies for this site/month/year
  const {
    data: existingSaisies,
    refetch: refetchSaisies,
    isLoading: saisiesLoading,
  } = useGetSaisies({
    siteId: currentSite?.id,
    mois: selectedMonth,
    annee: selectedYear,
    enabled: Boolean(isOpen && currentSite?.id && selectedMonth && selectedYear),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Debug the query parameters
  useEffect(() => {
    console.log("[DataEntryDialog] useGetSaisies parameters:", {
      isOpen,
      currentSiteId: currentSite?.id,
      selectedMonth,
      selectedYear,
      enabled: Boolean(isOpen && currentSite?.id && selectedMonth && selectedYear),
      saisiesLoading,
      existingSaisies: existingSaisies?.length,
    });
  }, [isOpen, currentSite?.id, selectedMonth, selectedYear, saisiesLoading, existingSaisies]);

  // Callback to handle refetching when needed
  const handleRefetch = useCallback(() => {
    if (currentSite?.id && selectedMonth && selectedYear) {
      console.log("[DataEntryDialog] Triggering refetch for:", {
        siteId: currentSite.id,
        month: selectedMonth,
        year: selectedYear,
      });
      refetchSaisies();
    }
  }, [refetchSaisies, currentSite?.id, selectedMonth, selectedYear]);

  // Create saisie mutation
  const createSaisieMutation = useCreateSaisie({
    onSuccess: async (data) => {
      console.log("Saisie created successfully:", data);
      alert("Saisie créée avec succès !");

      // Invalidate all saisies queries to force fresh data fetch
      console.log("Invalidating saisies cache after create...");
      await queryClient.invalidateQueries({ queryKey: ["saisies"] });

      // Also invalidate specific queries for this site/month/year
      await queryClient.invalidateQueries({
        queryKey: ["saisies", currentSite?.id, selectedMonth, selectedYear],
      });

      // Manually refetch the saisies to get the updated data
      console.log("Refetching saisies after create...");
      const refetchResult = await refetchSaisies();
      console.log("Refetch result after create:", {
        data: refetchResult.data?.map((s) => ({
          id: s.id,
          site: s.site,
          mois: s.mois,
          annee: s.annee,
        })),
        error: refetchResult.error,
        isSuccess: refetchResult.isSuccess,
      });
    },
    onError: (error) => {
      console.error("Error creating saisie:", error);
      alert(`Erreur lors de la création: ${error.message}`);
    },
  });

  // Update saisie mutation
  const updateSaisieMutation = useUpdateSaisie({
    onSuccess: async (data) => {
      console.log("Saisie updated successfully:", data);
      alert("Saisie mise à jour avec succès !");

      // Invalidate all saisies queries to force fresh data fetch
      console.log("Invalidating saisies cache after update...");
      await queryClient.invalidateQueries({ queryKey: ["saisies"] });

      // Also invalidate specific queries for this site/month/year
      await queryClient.invalidateQueries({
        queryKey: ["saisies", currentSite?.id, selectedMonth, selectedYear],
      });

      // Manually refetch the saisies to get the updated data
      console.log("Refetching saisies after update...");
      const refetchResult = await refetchSaisies();
      console.log("Refetch result after update:", {
        data: refetchResult.data?.map((s) => ({
          id: s.id,
          site: s.site,
          mois: s.mois,
          annee: s.annee,
          valeursCount: Object.values(s.postes_data || {}).reduce(
            (total, valeurs) => total + valeurs.length,
            0
          ),
        })),
        error: refetchResult.error,
        isSuccess: refetchResult.isSuccess,
      });
    },
    onError: (error) => {
      console.error("Error updating saisie:", error);
      alert(`Erreur lors de la mise à jour: ${error.message}`);
    },
  });

  // Organize indicators by poste
  const posteWithIndicators = useMemo((): PosteWithIndicators[] => {
    console.log("[DataEntryDialog] Building posteWithIndicators:", {
      hasPostesEmission: !!postesEmission,
      postesCount: postesEmission?.length || 0,
      hasTypeIndicateurs: !!typeIndicateurs,
      indicateursCount: typeIndicateurs?.length || 0,
      siteConfigJson: currentSite?.config_json,
      siteConfigType: typeof currentSite?.config_json,
      siteConfigIsArray: Array.isArray(currentSite?.config_json),
      siteConfigLength: Array.isArray(currentSite?.config_json)
        ? currentSite.config_json.length
        : 0,
    });

    if (!postesEmission || !typeIndicateurs) return [];

    // Use site config_json which contains obligatoire information
    const siteConfig = normalizeSiteConfigJson(currentSite?.config_json);

    console.log("[DataEntryDialog] After normalization:", {
      originalConfig: currentSite?.config_json,
      normalizedConfig: siteConfig,
      normalizedLength: siteConfig.length,
    });

    if (siteConfig.length === 0) {
      console.warn(
        "[DataEntryDialog] Normalized config is empty!",
        "Original config:",
        currentSite?.config_json
      );
      return [];
    }

    // Create a map of indicators by ID for quick lookup
    const indicatorsMap = new Map<number, TypeIndicateur>(
      (typeIndicateurs || []).map((indicator: TypeIndicateur) => [indicator.id, indicator])
    );

    // Create a map of postes by ID for quick lookup
    const postesMap = new Map(postesEmission.map((poste) => [poste.id, poste]));

    console.log("[DataEntryDialog] Maps created:", {
      indicatorsMapSize: indicatorsMap.size,
      indicatorIds: Array.from(indicatorsMap.keys()),
      postesMapSize: postesMap.size,
      posteIds: Array.from(postesMap.keys()),
      siteConfigStructure: siteConfig.map((config) => ({
        poste: config.poste,
        indicateursCount: config.indicateurs?.length || 0,
        indicateurs: config.indicateurs,
      })),
    });

    // Build the structure from site config
    return siteConfig
      .map((config) => {
        const poste = postesMap.get(config.poste);
        if (!poste) {
          console.log(
            "[DataEntryDialog] Poste not found:",
            config.poste,
            "Available postes:",
            Array.from(postesMap.keys())
          );
          return null;
        }

        // Handle both detailed config (with obligatoire) and basic config
        const configIndicateurs = Array.isArray(config.indicateurs) ? config.indicateurs : [];

        type IndicatorWithObligatoire = TypeIndicateur & {
          obligatoire?: boolean;
        };

        const indicators = configIndicateurs
          .map((indicatorItem: any): IndicatorWithObligatoire | null => {
            // Handle both formats: number (basic) or object (detailed)
            const indicatorIdRaw =
              typeof indicatorItem === "number"
                ? indicatorItem
                : (indicatorItem?.id ?? indicatorItem?.indicateur_id);
            const indicatorId =
              typeof indicatorIdRaw === "number" ? indicatorIdRaw : Number(indicatorIdRaw);

            if (!Number.isFinite(indicatorId)) return null;

            const obligatoire =
              typeof indicatorItem === "object" ? Boolean(indicatorItem?.obligatoire) : false;

            const indicator = indicatorsMap.get(indicatorId);
            if (!indicator) {
              console.log(
                "[DataEntryDialog] Indicator not found:",
                indicatorId,
                "Available indicators:",
                Array.from(indicatorsMap.keys())
              );
              return null;
            }

            return {
              ...indicator,
              obligatoire,
            };
          })
          .filter((indicator): indicator is IndicatorWithObligatoire => indicator !== null);

        return {
          poste: {
            id: poste.id,
            name: poste.name,
          },
          indicators: indicators,
        };
      })
      .filter((item): item is PosteWithIndicators => Boolean(item))
      .filter((item) => item.indicators.length > 0);
  }, [postesEmission, typeIndicateurs, currentSite?.config_json]);

  // Fetch dimensions for the active poste
  const { data: posteDimensions } = useGetPosteDimensions({
    posteId: activePosteId || 0,
    enabled: !!activePosteId,
  });

  // Set first poste as active when data changes
  useEffect(() => {
    if (posteWithIndicators.length > 0 && !activePosteId) {
      setActivePosteId(posteWithIndicators[0].poste.id);
    }
  }, [posteWithIndicators, activePosteId]);

  // Check for existing saisie and load data
  useEffect(() => {
    console.log("[DataEntryDialog] Existing saisies effect triggered:", {
      existingSaisies: existingSaisies?.length,
      existingSaisiesData: existingSaisies?.map((s) => ({
        id: s.id,
        site: s.site,
        mois: s.mois,
        annee: s.annee,
        valeursCount: Object.values(s.postes_data || {}).reduce(
          (total, valeurs) => total + valeurs.length,
          0
        ),
      })),
      site: site?.id,
      month: selectedMonth,
      year: selectedYear,
      isOpen,
      saisiesLoading,
      timestamp: new Date().toISOString(),
    });

    if (!isOpen || !site?.id || !selectedMonth || !selectedYear || saisiesLoading) {
      console.log("[DataEntryDialog] Effect skipped due to conditions:", {
        isOpen,
        siteId: site?.id,
        month: selectedMonth,
        year: selectedYear,
        loading: saisiesLoading,
      });
      return;
    }

    console.log("[DataEntryDialog] Processing saisies data...");

    if (existingSaisies && existingSaisies.length > 0) {
      console.log("[DataEntryDialog] Searching for matching saisie...");
      // Find the saisie that matches current site/month/year exactly
      const matchingSaisie = existingSaisies.find(
        (saisie) =>
          saisie.site === site.id && saisie.mois === selectedMonth && saisie.annee === selectedYear
      );

      if (matchingSaisie) {
        const valeursCount = Object.values(matchingSaisie.postes_data || {}).reduce(
          (total, valeurs) => total + valeurs.length,
          0
        );
        console.log("[DataEntryDialog] Found matching existing saisie:", {
          id: matchingSaisie.id,
          site: matchingSaisie.site,
          mois: matchingSaisie.mois,
          annee: matchingSaisie.annee,
          valeursCount,
          postes_data: matchingSaisie.postes_data,
        });
        setExistingSaisie(matchingSaisie);

        // Load existing values into the form from postes_data
        const newIndicatorData: IndicatorData = {};
        console.log("[DataEntryDialog] Loading postes_data:", matchingSaisie.postes_data);
        Object.values(matchingSaisie.postes_data || {}).forEach((posteValeurs) => {
          console.log("[DataEntryDialog] Processing poste with values:", posteValeurs);
          posteValeurs.forEach((valeur) => {
            if (!newIndicatorData[valeur.champ_metier]) {
              newIndicatorData[valeur.champ_metier] = {
                entries: [],
                isDraft: false,
              };
            }
            const newEntry = {
              id: `${valeur.champ_metier}-${Date.now()}-${Math.random()}`,
              value: valeur.valeur.toString(),
              selections: valeur.selections || [],
            };
            console.log(
              `[DataEntryDialog] Adding entry for champ_metier ${valeur.champ_metier}:`,
              newEntry
            );
            newIndicatorData[valeur.champ_metier].entries.push(newEntry);
          });
        });

        console.log(
          "[DataEntryDialog] Final indicator data with entry counts:",
          Object.entries(newIndicatorData).map(([id, data]) => ({
            indicatorId: id,
            entryCount: data.entries.length,
            entries: data.entries,
          }))
        );
        setIndicatorData(newIndicatorData);
      } else {
        console.log(
          "[DataEntryDialog] No matching saisie found, preparing for new entry. Available saisies:",
          existingSaisies.map((s) => ({
            id: s.id,
            site: s.site,
            mois: s.mois,
            annee: s.annee,
          }))
        );
        setExistingSaisie(null);
        setIndicatorData({});
      }
    } else {
      console.log("[DataEntryDialog] No existing saisies found - empty array or null");
      setExistingSaisie(null);
      setIndicatorData({});
    }
  }, [existingSaisies, site?.id, selectedMonth, selectedYear, isOpen, saisiesLoading]);

  // Handle manual date changes - let normal data loading handle everything
  useEffect(() => {
    // Just update the tracking refs, no need to clear data
    // The useGetSaisies hook and existing saisie effect will handle the rest
    previousMonth.current = selectedMonth;
    previousYear.current = selectedYear;
  }, [selectedMonth, selectedYear]);

  // Handle dialog opening - clear form only on initial open
  useEffect(() => {
    if (isOpen && isInitialOpen.current) {
      console.log("[DataEntryDialog] Dialog opened for first time - clearing form and setting up");

      // Clear form data on initial open
      setIndicatorData({});
      setExistingSaisie(null);

      // Only set to current date on first open if no date is set
      if (!selectedMonth || !selectedYear) {
        const currentDate = new Date();
        setSelectedMonth(currentDate.getMonth() + 1);
        setSelectedYear(currentDate.getFullYear());
      }

      isInitialOpen.current = false; // Mark as no longer initial open
    }
  }, [isOpen, selectedMonth, selectedYear]);

  // Trigger refetch when dialog opens or when month/year changes
  useEffect(() => {
    if (isOpen && currentSite?.id && selectedMonth && selectedYear) {
      console.log("[DataEntryDialog] Requesting refetch for:", {
        siteId: currentSite.id,
        month: selectedMonth,
        year: selectedYear,
      });
      handleRefetch();
    }
  }, [isOpen, currentSite?.id, selectedMonth, selectedYear, handleRefetch]);

  const handleInputChange = (indicatorId: number, entryId: string, value: string) => {
    setIndicatorData((prev) => {
      const currentData = prev[indicatorId] || { entries: [], isDraft: false };
      const updatedEntries = currentData.entries.map((entry) =>
        entry.id === entryId ? { ...entry, value } : entry
      );
      return {
        ...prev,
        [indicatorId]: {
          ...currentData,
          entries: updatedEntries,
        },
      };
    });
  };

  const handleAddEntry = (indicatorId: number) => {
    setIndicatorData((prev) => {
      const currentData = prev[indicatorId] || { entries: [], isDraft: false };
      const newEntry: IndicatorEntry = {
        id: `${indicatorId}-${Date.now()}-${Math.random()}`,
        value: "",
        selections: [],
      };
      return {
        ...prev,
        [indicatorId]: {
          ...currentData,
          entries: [...currentData.entries, newEntry],
        },
      };
    });
  };

  const handleRemoveEntry = (indicatorId: number, entryId: string) => {
    setIndicatorData((prev) => {
      const currentData = prev[indicatorId];
      if (!currentData) return prev;
      const updatedEntries = currentData.entries.filter((entry) => entry.id !== entryId);
      if (updatedEntries.length === 0) {
        const { [indicatorId]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [indicatorId]: {
          ...currentData,
          entries: updatedEntries,
        },
      };
    });
  };

  const handleDimensionChange = (
    indicatorId: number,
    entryId: string,
    dimensionId: number,
    valeurId: number | null
  ) => {
    setIndicatorData((prev) => {
      const currentData = prev[indicatorId] || { entries: [], isDraft: false };
      const updatedEntries = currentData.entries.map((entry) => {
        if (entry.id !== entryId) return entry;

        let newSelections = [...entry.selections];

        if (valeurId === null) {
          // Remove this dimension selection and all child dimensions
          const dimensionIndex = posteDimensions?.findIndex((d) => d.id === dimensionId);
          if (dimensionIndex !== undefined && dimensionIndex >= 0) {
            // Remove this dimension and all subsequent ones
            const dimensionsToRemove = posteDimensions?.slice(dimensionIndex).map((d) => d.id);
            newSelections = newSelections.filter(
              (s) => !dimensionsToRemove?.includes(s.dimension_id)
            );
          } else {
            // Fallback: just remove this dimension
            newSelections = newSelections.filter((s) => s.dimension_id !== dimensionId);
          }
        } else {
          // Find the index of the current dimension
          const dimensionIndex = posteDimensions?.findIndex((d) => d.id === dimensionId);

          // Update or add this dimension selection
          const existingIndex = newSelections.findIndex((s) => s.dimension_id === dimensionId);
          if (existingIndex >= 0) {
            newSelections[existingIndex] = {
              dimension_id: dimensionId,
              valeur_id: valeurId,
            };
          } else {
            newSelections.push({
              dimension_id: dimensionId,
              valeur_id: valeurId,
            });
          }

          // Clear all subsequent dimension selections (child dimensions)
          if (dimensionIndex !== undefined && dimensionIndex >= 0 && posteDimensions) {
            const subsequentDimensionIds = posteDimensions
              .slice(dimensionIndex + 1)
              .map((d) => d.id);
            newSelections = newSelections.filter(
              (s) => !subsequentDimensionIds.includes(s.dimension_id)
            );
          }
        }

        return { ...entry, selections: newSelections };
      });

      return {
        ...prev,
        [indicatorId]: {
          ...currentData,
          entries: updatedEntries,
        },
      };
    });
  };

  const handleSave = async (isDraft = false) => {
    if (!currentSite?.id || !selectedMonth || !selectedYear) {
      alert("Informations manquantes pour la sauvegarde");
      return;
    }

    // Convert entries to postes_data structure
    const postesData: PostesData = {};

    // Iterate through all configured indicators and collect their entries
    posteWithIndicators.forEach(({ poste, indicators }) => {
      indicators.forEach((indicator) => {
        const inputData = indicatorData[indicator.id];
        if (inputData && inputData.entries?.length > 0) {
          // Process each entry for this indicator
          inputData.entries.forEach((entry) => {
            if (entry.value?.trim()) {
              const numericValue = parseFloat(entry.value);
              if (!isNaN(numericValue)) {
                const posteId = poste.id;
                if (!postesData[posteId]) {
                  postesData[posteId] = [];
                }
                postesData[posteId].push({
                  champ_metier: indicator.id,
                  valeur: numericValue,
                  unite: indicator.unite_default || undefined,
                  selections: entry.selections,
                });
              }
            }
          });
        }
      });
    });

    // Check if we have any data to save
    const totalEntries = Object.values(postesData).reduce((sum, arr) => sum + arr.length, 0);
    if (totalEntries === 0) {
      alert("Aucune valeur saisie à enregistrer");
      return;
    }

    // TODO: Handle draft functionality if needed
    if (isDraft) {
      console.log("Draft functionality not yet implemented");
      return;
    }

    try {
      console.log("[DataEntryDialog] About to save:", {
        existingSaisie: existingSaisie?.id,
        site: currentSite.id,
        month: selectedMonth,
        year: selectedYear,
        entriesCount: totalEntries,
        postesData,
      });

      if (existingSaisie) {
        // Update existing saisie
        const updateData: UpdateSaisieRequest = {
          mois: selectedMonth,
          annee: selectedYear,
          postes_data: postesData,
        };
        console.log("Updating existing saisie:", existingSaisie.id, updateData);
        await updateSaisieMutation.mutateAsync({
          id: existingSaisie.id,
          data: updateData,
        });
      } else {
        // Create new saisie
        const createData: CreateSaisieRequest = {
          site: currentSite.id,
          mois: selectedMonth,
          annee: selectedYear,
          postes_data: postesData,
        };
        console.log("Creating new saisie:", createData);
        await createSaisieMutation.mutateAsync(createData);
      }
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error("Save failed:", error);
    }
  };

  const handleCancel = () => {
    setIndicatorData({});
    onClose();
  };

  const getMonthName = (month: number) => {
    const months = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    return months[month - 1];
  };
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  };

  const hasRequiredFields = () => {
    // Only check indicators that are marked as obligatory
    return posteWithIndicators.some((item) =>
      item.indicators.some((indicator) => {
        if (!indicator.obligatoire) return false;
        const entries = indicatorData[indicator.id]?.entries || [];
        return entries.length === 0 || entries.every((e) => !e.value?.trim());
      })
    );
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay">
      <div className="dialog-container">
        <div className="dialog-header">
          <div className="dialog-title-section">
            <h2 className="dialog-title">
              {existingSaisie ? "Modifier la Saisie" : "Nouvelle Saisie"}
            </h2>
            <p className="dialog-subtitle">
              Site: {currentSite?.name} • {userRole === "agent" ? "Mode Agent" : "Mode Admin"}
              {existingSaisie && (
                <span className="saisie-status"> • Statut: {existingSaisie.statut}</span>
              )}
            </p>
          </div>
          <button className="dialog-close" onClick={handleCancel}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content">
          {/* Date Selection */}
          <div className="date-selection">
            <div className="date-field">
              <label htmlFor="month-select">
                <Calendar size={16} />
                Mois
              </label>
              <SearchableCombobox
                items={Array.from({ length: 12 }, (_, i) => ({
                  value: i + 1,
                  label: getMonthName(i + 1),
                }))}
                value={selectedMonth}
                placeholder="Sélectionner un mois"
                onChange={(value) => setSelectedMonth(Number(value))}
              />
            </div>
            <div className="date-field">
              <label htmlFor="year-select">Année</label>
              <SearchableCombobox
                items={generateYearOptions().map((year) => ({
                  value: year,
                  label: String(year),
                }))}
                value={selectedYear}
                placeholder="Sélectionner une année"
                onChange={(value) => setSelectedYear(Number(value))}
              />
            </div>
          </div>

          {/* Indicators by Poste */}
          <div className="indicators-section">
            {!currentSite?.config_json ? (
              <div className="empty-indicators">
                <p>Site non configuré</p>
                <p>Ce site n'a pas encore été configuré avec des indicateurs.</p>
                <p>Contactez l'administrateur pour configurer les indicateurs de ce site.</p>
              </div>
            ) : normalizeSiteConfigJson(currentSite.config_json).length === 0 ? (
              <div className="empty-indicators">
                <p>Configuration vide</p>
                <p>Le site a été configuré mais aucun indicateur n'a été assigné.</p>
                <p>Contactez l'administrateur pour ajouter des indicateurs.</p>
              </div>
            ) : posteWithIndicators.length === 0 ? (
              <div className="empty-indicators">
                <p>Indicateurs non trouvés</p>
                <p>
                  La configuration existe mais les indicateurs référencés ne sont pas disponibles.
                </p>
                <p>Vérifiez la configuration du site avec l'administrateur.</p>
                <details
                  style={{
                    marginTop: "16px",
                    padding: "8px",
                    background: "#f3f4f6",
                    borderRadius: "4px",
                  }}
                >
                  <summary>Informations de débogage</summary>
                  <pre style={{ fontSize: "12px", marginTop: "8px" }}>
                    {JSON.stringify(
                      {
                        siteConfig: currentSite?.config_json,
                        availablePostes:
                          postesEmission?.map((p) => ({
                            id: p.id,
                            name: p.name,
                          })) || [],
                        availableIndicators:
                          typeIndicateurs?.map((i) => ({
                            id: i.id,
                            code: i.code,
                          })) || [],
                        hasPostes: !!postesEmission,
                        hasIndicators: !!typeIndicateurs,
                      },
                      null,
                      2
                    )}
                  </pre>
                </details>
              </div>
            ) : (
              <>
                {/* Tabs for postes */}
                <div className="postes-tabs">
                  {posteWithIndicators.map(({ poste, indicators }) => (
                    <button
                      key={poste.id}
                      className={`poste-tab ${activePosteId === poste.id ? "active" : ""}`}
                      onClick={() => setActivePosteId(poste.id)}
                    >
                      {poste.name}
                      <span className="poste-tab-badge">{indicators.length}</span>
                    </button>
                  ))}
                </div>

                {/* Active poste content */}
                <div className="postes-list">
                  {posteWithIndicators.map(({ poste, indicators }) => (
                    <div
                      key={poste.id}
                      className={`poste-panel ${activePosteId === poste.id ? "active" : ""}`}
                    >
                      <div className="poste-content">
                        <div className="indicators-grid">
                          {indicators.map((indicator) => {
                            const entries = indicatorData[indicator.id]?.entries || [];
                            const hasEntries = entries.length > 0;

                            // Debug logging
                            if (entries.length > 0) {
                              console.log(
                                `[DataEntryDialog] Rendering indicator ${indicator.id} (${indicator.code}) with ${entries.length} entries:`,
                                entries
                              );
                            }

                            return (
                              <div key={indicator.id} className="indicator-card">
                                <div className="indicator-header">
                                  <div className="indicator-label">
                                    <span className="indicator-code">{indicator.code}</span>
                                    <span className="indicator-name">{indicator.libelle}</span>
                                    {indicator.obligatoire && (
                                      <span className="required-badge">*</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="add-entry-btn"
                                    onClick={() => handleAddEntry(indicator.id)}
                                    disabled={
                                      createSaisieMutation.isPending ||
                                      updateSaisieMutation.isPending
                                    }
                                    title="Ajouter une entrée"
                                  >
                                    + Ajouter
                                  </button>
                                </div>

                                {/* Entries list */}
                                <div className="entries-container">
                                  {!hasEntries ? (
                                    <div className="empty-entry-placeholder">
                                      Cliquez sur "+ Ajouter" pour créer une entrée
                                    </div>
                                  ) : (
                                    entries.map((entry, index) => (
                                      <div key={entry.id} className="indicator-entry">
                                        <div className="entry-content">
                                          {/* Dimension selectors for this entry */}
                                          {posteDimensions && posteDimensions.length > 0 && (
                                            <div className="dimensions-row">
                                              {posteDimensions.map((dimension, dimIndex) => {
                                                const selectedValue = entry.selections.find(
                                                  (s) => s.dimension_id === dimension.id
                                                )?.valeur_id;

                                                // Filter dimension values based on parent selection
                                                let filteredValeurs = dimension.valeurs;
                                                if (dimIndex > 0) {
                                                  // Get the previous dimension's selected value
                                                  const previousDimension =
                                                    posteDimensions[dimIndex - 1];
                                                  const previousSelectedValue =
                                                    entry.selections.find(
                                                      (s) => s.dimension_id === previousDimension.id
                                                    )?.valeur_id;

                                                  if (previousSelectedValue) {
                                                    // Filter to show only values where parent matches previous selection
                                                    filteredValeurs = dimension.valeurs.filter(
                                                      (v) => v.parent === previousSelectedValue
                                                    );
                                                  } else {
                                                    // If previous dimension not selected, show values with no parent
                                                    filteredValeurs = dimension.valeurs.filter(
                                                      (v) => v.parent === null
                                                    );
                                                  }
                                                } else {
                                                  // First dimension: show only values with no parent
                                                  filteredValeurs = dimension.valeurs.filter(
                                                    (v) => v.parent === null
                                                  );
                                                }

                                                return (
                                                  <div
                                                    key={dimension.id}
                                                    className="dimension-field"
                                                  >
                                                    <label className="dimension-label">
                                                      {dimension.name}
                                                    </label>
                                                    <SearchableCombobox
                                                      items={filteredValeurs.map((valeur) => ({
                                                        value: valeur.id,
                                                        label: valeur.valeur,
                                                      }))}
                                                      value={selectedValue || ""}
                                                      placeholder="Sélectionner..."
                                                      onChange={(value) =>
                                                        handleDimensionChange(
                                                          indicator.id,
                                                          entry.id,
                                                          dimension.id,
                                                          value ? Number(value) : null
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}

                                          {/* Value input */}
                                          <div className="value-input-row">
                                            <input
                                              type="number"
                                              step="any"
                                              placeholder="Valeur"
                                              value={entry.value || ""}
                                              onChange={(e) =>
                                                handleInputChange(
                                                  indicator.id,
                                                  entry.id,
                                                  e.target.value
                                                )
                                              }
                                              onWheel={(e) => e.currentTarget.blur()}
                                              className="value-input"
                                              disabled={
                                                createSaisieMutation.isPending ||
                                                updateSaisieMutation.isPending
                                              }
                                            />
                                            <span className="value-unit">
                                              {indicator.unite_default}
                                            </span>
                                            <button
                                              type="button"
                                              className="remove-entry-btn"
                                              onClick={() =>
                                                handleRemoveEntry(indicator.id, entry.id)
                                              }
                                              disabled={
                                                createSaisieMutation.isPending ||
                                                updateSaisieMutation.isPending
                                              }
                                              title="Supprimer cette entrée"
                                            >
                                              ×
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Dialog Actions */}
        <div className="dialog-actions">
          <button
            className="btn-cancel"
            onClick={handleCancel}
            disabled={createSaisieMutation.isPending || updateSaisieMutation.isPending}
          >
            <Ban size={16} />
            {existingSaisie ? "Fermer" : "Annuler"}
          </button>
          <button
            className="btn-save"
            onClick={() => handleSave(false)}
            disabled={
              createSaisieMutation.isPending ||
              updateSaisieMutation.isPending ||
              hasRequiredFields() ||
              !currentSite?.config_json
            }
          >
            <Save size={16} />
            {createSaisieMutation.isPending || updateSaisieMutation.isPending
              ? "Sauvegarde..."
              : existingSaisie
                ? "Mettre à jour"
                : "Sauvegarder"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataEntryDialog;
